import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  FaCheck,
  FaTimes,
  FaExclamationCircle,
  FaClipboardList,
  FaRupeeSign,
  FaCalendarAlt,
  FaLayerGroup,
  FaPlusCircle,
  FaEdit,
  FaClock,
  FaToggleOn,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";
import { classesList } from "../../jsonsFiles/classesList";

export default function FeeStructureForm({
  isOpen,
  onClose,
  structure,
  itemToEdit,
  onSuccess,
}) {
  const currentStructure = structure || itemToEdit;
  const isEdit = !!currentStructure;
  const { token } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      title: "",
      class: "1",
      amount: "",
      frequency: "Monthly",
      dueDate: new Date().toISOString().split("T")[0],
    },
  });

  useEffect(() => {
    if (currentStructure) {
      reset({
        ...currentStructure,
        dueDate: currentStructure.dueDate
          ? new Date(currentStructure.dueDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      });
    } else {
      reset({
        title: "",
        class: "1",
        amount: "",
        frequency: "Monthly",
        dueDate: new Date().toISOString().split("T")[0],
      });
    }
  }, [currentStructure, reset, isOpen]);

  if (!isOpen) return null;

  const onSubmit = async (data) => {
    try {
      const url = isEdit
        ? `${server}/fee-structures/${currentStructure.id || currentStructure.Id}`
        : `${server}/fee-structures`;

      const method = isEdit ? "put" : "post";

      const payload = {
        title: data.title,
        class: data.class,
        amount: parseFloat(data.amount),
        frequency: data.frequency,
        dueDate: new Date(data.dueDate).toISOString(),
      };

      await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success(
        isEdit ? "Fee Structure updated successfully!" : "New Fee Structure created!",
      );
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error saving fee structure.",
      );
      console.error(error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/65 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        
        {/* Royal Blue Header Bar */}
        <div className="bg-[#1d5bd8] text-white p-5 sm:p-6 flex items-center justify-between shrink-0 shadow-md">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-xs flex items-center justify-center text-white font-black shadow-xs border border-white/20">
              {isEdit ? <FaEdit size={18} /> : <FaPlusCircle size={18} />}
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black tracking-tight text-white leading-tight">
                {isEdit ? "Edit Fee Structure" : "New Fee Structure"}
              </h2>
              <p className="text-xs text-blue-100 font-medium mt-0.5">
                {isEdit ? "Update fee rule and installment terms" : "Configure class-wise fee rules and payment schedules"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-2xl bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition cursor-pointer"
            aria-label="Close modal"
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* Form Fields */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fee Title */}
            <div className="col-span-1 md:col-span-2 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Fee Title <span className="text-rose-500">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaClipboardList size={14} />
                </div>
                <input
                  {...register("title", { required: "Fee title is required" })}
                  className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border ${
                    errors.title
                      ? "border-rose-500 bg-rose-50/50"
                      : "border-gray-200 dark:border-slate-700 focus:border-blue-600 dark:focus:border-blue-500"
                  } rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-bold text-gray-900 dark:text-white transition-all`}
                  placeholder="e.g. Annual Tuition Fee, Term 1 Exam Fee"
                />
                {errors.title && (
                  <div className="absolute right-3 top-3 text-rose-500">
                    <FaExclamationCircle size={14} />
                  </div>
                )}
              </div>
              {errors.title && (
                <p className="text-xs text-rose-500 font-bold ml-1 animate-pulse">
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* Target Class */}
            <div className="col-span-1 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Target Class <span className="text-rose-500">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaLayerGroup size={14} />
                </div>
                <select
                  {...register("class", { required: "Class is required" })}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 focus:border-blue-600 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-bold text-gray-900 dark:text-white transition-all cursor-pointer"
                >
                  {classesList.map((i) => (
                    <option key={i + 1} value={i}>
                      Class {i}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Amount */}
            <div className="col-span-1 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Fee Amount (₹) <span className="text-rose-500">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaRupeeSign size={13} />
                </div>
                <input
                  type="number"
                  step="0.01"
                  {...register("amount", { required: "Amount is required" })}
                  className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border ${
                    errors.amount
                      ? "border-rose-500 bg-rose-50/50"
                      : "border-gray-200 dark:border-slate-700 focus:border-blue-600 dark:focus:border-blue-500"
                  } rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-bold text-gray-900 dark:text-white transition-all`}
                  placeholder="0.00"
                />
                {errors.amount && (
                  <div className="absolute right-3 top-3 text-rose-500">
                    <FaExclamationCircle size={14} />
                  </div>
                )}
              </div>
              {errors.amount && (
                <p className="text-xs text-rose-500 font-bold ml-1 animate-pulse">
                  {errors.amount.message}
                </p>
              )}
            </div>

            {/* Frequency */}
            <div className="col-span-1 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Payment Frequency <span className="text-rose-500">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaClock size={14} />
                </div>
                <select
                  {...register("frequency", {
                    required: "Frequency is required",
                  })}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 focus:border-blue-600 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-bold text-gray-900 dark:text-white transition-all cursor-pointer"
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Half-Yearly">Half-Yearly</option>
                  <option value="Yearly">Yearly</option>
                  <option value="One-Time">One-Time</option>
                </select>
              </div>
            </div>

            {/* Default Due Date */}
            <div className="col-span-1 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Default Due Date <span className="text-rose-500">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaCalendarAlt size={14} />
                </div>
                <input
                  type="date"
                  {...register("dueDate", { required: "Due Date is required" })}
                  className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border ${
                    errors.dueDate
                      ? "border-rose-500 bg-rose-50/50"
                      : "border-gray-200 dark:border-slate-700 focus:border-blue-600 dark:focus:border-blue-500"
                  } rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-bold text-gray-900 dark:text-white transition-all`}
                />
              </div>
              {errors.dueDate && (
                <p className="text-xs text-rose-500 font-bold ml-1 animate-pulse">
                  {errors.dueDate.message}
                </p>
              )}
            </div>
          </div>

          {/* Active Status Toggle (Disabled / Enabled by Default) */}
          <div className="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-gray-200 dark:border-slate-700/70">
            <div>
              <span className="text-xs font-black text-gray-900 dark:text-white block">
                Fee Rule Active Status
              </span>
              <span className="text-[10px] font-semibold text-gray-500 dark:text-slate-400">
                Active status is enabled by default for all fee structure rules
              </span>
            </div>

            <button
              type="button"
              disabled={true}
              title="Fee rule active status is enabled by default"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-black text-xs opacity-75 cursor-not-allowed bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
            >
              <FaToggleOn size={16} />
              <span>Active</span>
            </button>
          </div>

          {/* Action Footer */}
          <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-gray-100 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-gray-600 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-xl transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 text-xs font-black text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md shadow-blue-500/20 disabled:opacity-50 transition flex items-center gap-2 cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaCheck size={11} /> {isEdit ? "Update Structure" : "Save Structure"}
                </>
              )}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
